'use client'
import Section from 'components/Section/Section'
import { useMemoizedContentGetter } from 'utils/contentful'
import { TitleDivider } from 'components/TitleDivider'
import { MediaImageStandard } from 'components/Contentful'
import { HelocProvider } from './HelocContext'
import { HelocForm } from './HelocForm'
import { FormDialog } from './FormDialog'
import { HelocOutput } from './HelocOutput'
import { HomeValue } from './HomeValue'
import { useVisibilityGroup } from 'hooks/useVisibilityGroup'
import { useRef, useState } from 'react'

const HelocCalculatorSection = ({ content }) => {
  const { eyebrow, title, subtitle, mainImage, outputImage, textContent } =
    useMemoizedContentGetter(content, [
      'eyebrow',
      'title',
      'subtitle',
      'mainImage',
      'outputImage',
      'textContent',
    ])

  const resultRef = useRef(null)
  const [recalculate, setRecalculate] = useState(false)
  const { ref } = useVisibilityGroup({}, true)
  const defaultValues = {
    homesCurrentValue: null,
    minimumHomesCurrentValue: 0,
    maximumHomesCurrentValue: 10000000000,
    mortgageOutstandingBalance: null,
    minimumMortgageOutstandingBalance: 0,
    maximumMortgageOutstandingBalance: 10000000000,
    desiredLoanAmount: null,
    minimumDesiredLoanAmount: 0,
    maximumDesiredLoanAmount: 10000000000,
    creditScore: 'excellent',
    liveState: '',
    selectItemList: [
      { label: 'Excellent (750+)', value: 'excellent' },
      { label: 'Good (680 - 749)', value: 'good' },
      { label: 'Average (620 - 679)', value: 'average' },
      { label: 'Below Average (<620)', value: 'below average' },
    ],
    homesCurrentValueInfo:
      "This is the current value of your home. You can get this from an appraiser, real estate broker, or by searching online for what's my home worth? and providing your address.",
    mortgageOutstandingBalanceInfo:
      'Most lenders require you to have at least 20% equity in your home to qualify for home equity financing.',
    desiredLoanAmountInfo:
      'How much would you like to borrow? Most home equity lenders allow you to borrow up to 80% of your property value including your first mortgage balance. The loan to value requirement can vary with each lender.',
    creditScoreInfo:
      'Lenders generally require a credit score of at least 620, and sometimes higher, to approve home equity financing.',
    loanAmountErrorMessage: '',
  }

  return (
    <>
      <div ref={ref}>
        <HelocProvider defaultValues={defaultValues}>
          <Section backgroundColor="none" key="main-section" data-testid="heloc-calculator-section">
            <div className="absolute top-0 right-0 w-full h-[270px] overflow-hidden -z-10 sm:block hidden">
              <MediaImageStandard
                className="absolute top-0 right-[-20%] h-full min-w-full object-cover sm:right-0 sm:w-full sm:h-[85%]"
                alt={mainImage.altText}
                content={mainImage.mediaContent}
                layout="cover"
              />
            </div>
            <div className="absolute top-0 right-0 w-full h-[270px] overflow-hidden -z-10 sm:hidden block">
              <MediaImageStandard
                className="absolute top-0 h-full min-w-full right-0 w-full h-[90%]"
                alt={mainImage.altText}
                content={mainImage.mobileMediaContent}
                layout="cover"
              />
            </div>
            <div className="flex flex-col gap-4 py-12">
              <TitleDivider
                className="mb-14 sm:mb-6"
                eyebrowBlack
                eyebrow={eyebrow}
                title={title}
                subtitle={subtitle}
                dataTestId="heloc-calculator-title-divider"
              />
              <div className="flex flex-col justify-evenly items-center lg:items-start w-full lg:flex-row [&>div:last-child]:max-w-full">
                <div className="border-solid border-2 border-[#D2E2FE] rounded-[10px] p-4 bg-[#EFF5FF] w-full lg:max-w-[35%] max-w-[95%] md:max-w-[93%]">
                  <HelocForm
                    setRecalculate={() => {
                      setRecalculate(!recalculate)
                    }}
                    buttonColor={true}
                    textContent={textContent?.jsonContent}
                    resultRef={resultRef}
                  />
                </div>
                <HelocOutput
                  recalculate={recalculate}
                  content={content}
                  outputImage={outputImage}
                  variant="updated"
                  helocUpdated={true}
                  textContent={textContent?.jsonContent}
                  ref={resultRef}
                />
              </div>
            </div>
            <FormDialog />
            <HomeValue helocUpdated={true} />
          </Section>
        </HelocProvider>
      </div>
    </>
  )
}

export { HelocCalculatorSection }
export default HelocCalculatorSection
